import React, { useEffect, useState } from 'react';
import { getFirestore, collection, query, getDocs, addDoc, onSnapshot, deleteDoc, doc, setDoc } from 'firebase/firestore';
import app from '../../firebase';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

const db = getFirestore(app);

export default function AddRoom() {
  const [roomName, setRoomName] = useState('');
  const [roomType, setRoomType] = useState('');
  const [roomTypeInput, setRoomTypeInput] = useState('');
  const [customRoomTypes, setCustomRoomTypes] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [roomSubjectInput, setRoomSubjectInput] = useState('');
  const [showSubjectSuggestions, setShowSubjectSuggestions] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [roomDepartmentInput, setRoomDepartmentInput] = useState('');
  const [showDepartmentSuggestions, setShowDepartmentSuggestions] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [roomStartTime, setRoomStartTime] = useState('');
  const [roomEndTime, setRoomEndTime] = useState('');
  const [operatingDays, setOperatingDays] = useState([]);
  const [specificDayDate, setSpecificDayDate] = useState('');
  const [specificDayStartTime, setSpecificDayStartTime] = useState('');
  const [specificDayEndTime, setSpecificDayEndTime] = useState('');
  const [specificDayEntries, setSpecificDayEntries] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomFilterName, setRoomFilterName] = useState('');
  const [roomFilterType, setRoomFilterType] = useState('');
  const [roomFilterSubject, setRoomFilterSubject] = useState('');
  const [roomFilterDepartment, setRoomFilterDepartment] = useState('');
  const [roomMsg, setRoomMsg] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingRoom, setIsEditingRoom] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [roomPendingDelete, setRoomPendingDelete] = useState(null);
  const [isDeletingRoom, setIsDeletingRoom] = useState(false);
  const [roomDeleteError, setRoomDeleteError] = useState('');
  const [setDefaultOperatingTime, setSetDefaultOperatingTime] = useState(false);
  const [setDefaultOperatingDays, setSetDefaultOperatingDays] = useState(false);
  const [setDefaultSpecificDay, setSetDefaultSpecificDay] = useState(false);
  



  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'curriculum_subjects')));
        setSubjects(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('Failed to fetch subjects', err);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'departments')));
        setDepartments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('Failed to fetch departments', err);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'rooms'), (snap) => {
      const roomList = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const aCreated = a.createdAt || '';
          const bCreated = b.createdAt || '';
          return aCreated.localeCompare(bCreated);
        });
      setRooms(roomList);
      const savedTypes = Array.from(new Set(roomList.map(room => room.roomType).filter(Boolean)));
      setCustomRoomTypes(prevTypes => Array.from(new Set([...prevTypes, ...savedTypes])));
    }, (err) => {
      console.error('Failed to load rooms', err);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('roomFormDefaults');
      if (stored) {
        const defaults = JSON.parse(stored);
        if (defaults) {
          if (defaults.roomStartTime) setRoomStartTime(defaults.roomStartTime);
          if (defaults.roomEndTime) setRoomEndTime(defaults.roomEndTime);
          if (Array.isArray(defaults.operatingDays)) setOperatingDays(defaults.operatingDays);
          if (defaults.specificDayDate) setSpecificDayDate(defaults.specificDayDate);
          if (defaults.specificDayStartTime) setSpecificDayStartTime(defaults.specificDayStartTime);
          if (defaults.specificDayEndTime) setSpecificDayEndTime(defaults.specificDayEndTime);
          if (Array.isArray(defaults.specificDayEntries)) setSpecificDayEntries(defaults.specificDayEntries);
          setSetDefaultOperatingTime(!!defaults.setDefaultOperatingTime);
          setSetDefaultOperatingDays(!!defaults.setDefaultOperatingDays);
          setSetDefaultSpecificDay(!!defaults.setDefaultSpecificDay);
        }
      }
    } catch (err) {
      console.warn('Failed to load saved defaults', err);
    }
  }, []);

  const saveFormDefaults = (updates = {}) => {
    const defaults = {
      roomStartTime,
      roomEndTime,
      operatingDays,
      specificDayDate,
      specificDayStartTime,
      specificDayEndTime,
      specificDayEntries,
      setDefaultOperatingTime,
      setDefaultOperatingDays,
      setDefaultSpecificDay,
      ...updates
    };

    if (defaults.setDefaultOperatingTime || defaults.setDefaultOperatingDays || defaults.setDefaultSpecificDay) {
      localStorage.setItem('roomFormDefaults', JSON.stringify(defaults));
    } else {
      localStorage.removeItem('roomFormDefaults');
    }
  };

  
  useEffect(() => {
    if (setDefaultOperatingTime) {
      saveFormDefaults();
    }
  }, [roomStartTime, roomEndTime, setDefaultOperatingTime]);

  useEffect(() => {
    if (setDefaultOperatingDays) {
      saveFormDefaults();
    }
  }, [operatingDays, setDefaultOperatingDays]);

  useEffect(() => {
    if (setDefaultSpecificDay) {
      saveFormDefaults();
    }
  }, [specificDayDate, specificDayStartTime, specificDayEndTime, setDefaultSpecificDay]);

  useEffect(() => {
    if (setDefaultSpecificDay) {
      saveFormDefaults();
    }
  }, [specificDayEntries, setDefaultSpecificDay]);

  useEffect(() => {
    saveFormDefaults();
  }, [setDefaultOperatingTime, setDefaultOperatingDays, setDefaultSpecificDay]);

  const getSavedFormDefaults = () => {
    try {
      const stored = localStorage.getItem('roomFormDefaults');
      return stored ? JSON.parse(stored) : {};
    } catch (err) {
      console.warn('Failed to parse saved defaults', err);
      return {};
    }
  };

  const selectedSubjects = subjects.filter(s => selectedSubjectIds.includes(s.id));
  const selectedDepartments = departments.filter(d => selectedDepartmentIds.includes(d.id));

  const subjectSuggestions = subjects.filter(s => {
    const display = `${s.courseId || ''}${s.courseId ? ' - ' : ''}${s.courseName || ''}`.toLowerCase();
    return roomSubjectInput.trim() === '' || display.includes(roomSubjectInput.trim().toLowerCase());
  });

  const departmentSuggestions = departments.filter(d => {
    const display = (d.name || '').toLowerCase();
    return roomDepartmentInput.trim() === '' || display.includes(roomDepartmentInput.trim().toLowerCase());
  });

  const formatTime12Hour = (time) => {
    if (!time) return '';
    const [hourStr, minute] = time.split(':');
    if (!hourStr || !minute) return time;
    const hour = parseInt(hourStr, 10);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const adjustedHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${adjustedHour}:${minute} ${suffix}`;
  };

  const handleToggleSubject = (subjectId) => {
    setSelectedSubjectIds(prev =>
      prev.includes(subjectId) ? prev.filter(id => id !== subjectId) : [...prev, subjectId]
    );
    setRoomSubjectInput('');
    setShowSubjectSuggestions(false);
  };

  const handleToggleDepartment = (departmentId) => {
    setSelectedDepartmentIds(prev =>
      prev.includes(departmentId) ? prev.filter(id => id !== departmentId) : [...prev, departmentId]
    );
    setRoomDepartmentInput('');
    setShowDepartmentSuggestions(false);
  };

  const handleRemoveSubject = (subjectId) => {
    setSelectedSubjectIds(prev => prev.filter(id => id !== subjectId));
  };

  const isValidTimeRange = (startTime, endTime) => {
    const toMinutes = value => {
      const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value || '');
      return match ? Number(match[1]) * 60 + Number(match[2]) : NaN;
    };
    return toMinutes(startTime) < toMinutes(endTime);
  };

  const toggleOperatingDay = (day) => {
    setOperatingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleAddSpecificDayEntry = () => {
    if (!specificDayDate || !specificDayStartTime || !specificDayEndTime) {
      setRoomMsg('Please provide a specific date and both start/end times.');
      return;
    }
    if (!isValidTimeRange(specificDayStartTime, specificDayEndTime)) {
      setRoomMsg('The specific-day end time must be later than its start time.');
      return;
    }
    setSpecificDayEntries(prev => {
      const existing = prev.find(entry => entry.date === specificDayDate);
      if (existing) {
        return prev.map(entry => entry.date === specificDayDate ? { date: specificDayDate, startTime: specificDayStartTime, endTime: specificDayEndTime } : entry);
      }
      return [...prev, { date: specificDayDate, startTime: specificDayStartTime, endTime: specificDayEndTime }];
    });
    setSpecificDayDate('');
    setSpecificDayStartTime('');
    setSpecificDayEndTime('');
    setRoomMsg('');
  };

  const handleRemoveSpecificDayEntry = (date) => {
    setSpecificDayEntries(prev => prev.filter(entry => entry.date !== date));
  };

  const filteredRooms = rooms.filter(room => {
    const matchesName = roomFilterName.trim() === '' || (room.roomName || '').toLowerCase().includes(roomFilterName.trim().toLowerCase());
    const matchesType = !roomFilterType || (room.roomType || '') === roomFilterType;
    const subjectNames = Array.isArray(room.roomSubjectNames) ? room.roomSubjectNames : [room.roomSubjectName].filter(Boolean);
    const departmentNames = Array.isArray(room.departmentAssignedNames) ? room.departmentAssignedNames : [];
    const matchesSubject = !roomFilterSubject || subjectNames.includes(roomFilterSubject);
    const matchesDepartment = !roomFilterDepartment || departmentNames.includes(roomFilterDepartment);
    return matchesName && matchesType && matchesSubject && matchesDepartment;
  });

  const handleAddRoomType = () => {
    const newType = roomTypeInput.trim();
    if (newType && !customRoomTypes.includes(newType)) {
      setCustomRoomTypes(prev => [...prev, newType]);
      setRoomType(newType);
      setRoomTypeInput('');
    }
  };

  const restoreDefaults = () => {
    if (!setDefaultOperatingTime) {
      setRoomStartTime('');
      setRoomEndTime('');
    }
    if (!setDefaultOperatingDays) {
      setOperatingDays([]);
    }
    if (!setDefaultSpecificDay) {
      setSpecificDayDate('');
      setSpecificDayStartTime('');
      setSpecificDayEndTime('');
    }
  };

  const handleSaveRoom = async (e) => {
    e.preventDefault();
    setRoomMsg('');

    if (!roomName.trim() || !roomType.trim()) {
      setRoomMsg('Room name and room type are required.');
      return;
    }
    if (Boolean(roomStartTime) !== Boolean(roomEndTime) || (roomStartTime && !isValidTimeRange(roomStartTime, roomEndTime))) {
      setRoomMsg('Provide both operating times, and make sure the end time is later than the start time.');
      return;
    }
    if (specificDayEntries.some(entry => !isValidTimeRange(entry.startTime, entry.endTime))) {
      setRoomMsg('Every specific-day end time must be later than its start time.');
      return;
    }

    const normalizedRoomName = roomName.trim().toLowerCase();
    const duplicate = rooms.some(
      room => (room.roomName || '').trim().toLowerCase() === normalizedRoomName && room.id !== editingRoomId
    );

    if (duplicate) {
      setRoomMsg('A room with that name already exists.');
      return;
    }

    setIsSaving(true);

    try {
      const trimmedType = roomType.trim();
      if (trimmedType && !customRoomTypes.includes(trimmedType)) {
        setCustomRoomTypes(prev => Array.from(new Set([...prev, trimmedType])));
      }

      const departmentNames = departments.filter(d => selectedDepartmentIds.includes(d.id)).map(d => d.name);
      if (isEditingRoom && editingRoomId) {
        await setDoc(doc(db, 'rooms', editingRoomId), {
          roomName: roomName.trim(),
          roomType: trimmedType,
          roomSubjectIds: selectedSubjectIds,
          roomSubjectNames: selectedSubjects.map(s => `${s.courseId || ''} - ${s.courseName || ''}`),
          departmentAssignedIds: selectedDepartmentIds,
          departmentAssignedNames: departmentNames,
          departments: departmentNames,
          roomStartTime: roomStartTime || '',
          roomEndTime: roomEndTime || '',
          operatingDays,
          specificDayEntries,
          setDefaultOperatingTime,
          setDefaultOperatingDays,
          setDefaultSpecificDay
        }, { merge: true });
        setRoomMsg('Room updated successfully.');
        setIsEditingRoom(false);
        setEditingRoomId(null);
      } else {
        await addDoc(collection(db, 'rooms'), {
          roomName: roomName.trim(),
          roomType: trimmedType,
          roomSubjectIds: selectedSubjectIds,
          roomSubjectNames: selectedSubjects.map(s => `${s.courseId || ''} - ${s.courseName || ''}`),
          departmentAssignedIds: selectedDepartmentIds,
          departmentAssignedNames: departmentNames,
          departments: departmentNames,
          roomStartTime: roomStartTime || '',
          roomEndTime: roomEndTime || '',
          operatingDays,
          specificDayEntries,
          setDefaultOperatingTime,
          setDefaultOperatingDays,
          setDefaultSpecificDay,
          createdAt: new Date().toISOString()
        });
        setRoomMsg('Room added successfully.');
      }
      
      setRoomName('');
      setRoomType('');
      setSelectedSubjectIds([]);
      setSelectedDepartmentIds([]);
      if (!setDefaultOperatingTime) {
        setRoomStartTime('');
        setRoomEndTime('');
      }
      if (!setDefaultOperatingDays) {
        setOperatingDays([]);
      }
      if (!setDefaultSpecificDay) {
        setSpecificDayDate('');
        setSpecificDayStartTime('');
        setSpecificDayEndTime('');
      }
      setSpecificDayEntries([]);
    } catch (err) {
      setRoomMsg(err.message || 'Failed to save room.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditRoom = (room) => {
    const savedDefaults = getSavedFormDefaults();
    setRoomName(room.roomName || '');
    setRoomType(room.roomType || '');
    setSelectedSubjectIds(room.roomSubjectIds || []);
    const savedDepartmentIds = Array.isArray(room.departmentAssignedIds) ? room.departmentAssignedIds : [];
    setSelectedDepartmentIds(savedDepartmentIds.length > 0
      ? savedDepartmentIds
      : room.departmentAssignedId
        ? [room.departmentAssignedId]
        : departments.filter(department =>
          (room.departmentAssignedNames || room.departments || []).includes(department.name)
        ).map(department => department.id));
    setRoomStartTime(
      room.roomStartTime || (room.setDefaultOperatingTime ? savedDefaults.roomStartTime || '' : '')
    );
    setRoomEndTime(
      room.roomEndTime || (room.setDefaultOperatingTime ? savedDefaults.roomEndTime || '' : '')
    );
    setOperatingDays(room.operatingDays || []);
    if ((!room.operatingDays || !room.operatingDays.length) && room.setDefaultOperatingDays) {
      setOperatingDays(Array.isArray(savedDefaults.operatingDays) ? savedDefaults.operatingDays : []);
    }
    setSpecificDayEntries(room.specificDayEntries || []);
    if ((!room.specificDayEntries || !room.specificDayEntries.length) && room.setDefaultSpecificDay) {
      setSpecificDayEntries(Array.isArray(savedDefaults.specificDayEntries) ? savedDefaults.specificDayEntries : []);
    }
    setSetDefaultOperatingTime(!!room.setDefaultOperatingTime);
    setSetDefaultOperatingDays(!!room.setDefaultOperatingDays);
    setSetDefaultSpecificDay(!!room.setDefaultSpecificDay);
    setIsEditingRoom(true);
    setEditingRoomId(room.id);
  };

  const handleCancelEdit = () => {
    setRoomName('');
    setRoomType('');
    setSelectedSubjectIds([]);
    setSelectedDepartmentIds([]);
    if (!setDefaultOperatingTime) {
      setRoomStartTime('');
      setRoomEndTime('');
    }
    if (!setDefaultOperatingDays) {
      setOperatingDays([]);
    }
    if (!setDefaultSpecificDay) {
      setSpecificDayDate('');
      setSpecificDayStartTime('');
      setSpecificDayEndTime('');
    }
    setSpecificDayEntries([]);
    setRoomTypeInput('');
    setRoomMsg('');
    setIsEditingRoom(false);
    setEditingRoomId(null);
  };

  const handleDeleteRoom = (room) => {
    setRoomDeleteError('');
    setRoomPendingDelete(room);
  };

  const confirmDeleteRoom = async () => {
    if (!roomPendingDelete || isDeletingRoom) return;
    setIsDeletingRoom(true);
    try {
      await deleteDoc(doc(db, 'rooms', roomPendingDelete.id));
      setRoomPendingDelete(null);
    } catch (err) {
      setRoomDeleteError('Failed to delete room: ' + (err.message || err));
    } finally {
      setIsDeletingRoom(false);
    }
  };

  const toggleActionMenu = (roomId) => {
    setActiveActionMenu(prev => (prev === roomId ? null : roomId));
  };

  return (
    <div>
      {isEditingRoom && (
        <div
          role="presentation"
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)' }}
        />
      )}
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          margin: '0 auto 24px',
          maxWidth: 1100,
          ...(isEditingRoom ? {
            position: 'fixed',
            zIndex: 1001,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(1100px, calc(100vw - 32px))',
            maxHeight: 'calc(100vh - 32px)',
            overflowY: 'auto',
            boxSizing: 'border-box'
          } : {})
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 600, color: '#155c2b', marginBottom: 24, textAlign: 'left' }}>
          {isEditingRoom ? 'Edit Room' : 'Add Room'}
        </h2>
        <form onSubmit={handleSaveRoom} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 32, alignItems: 'stretch' }}>
          <div style={{ display: 'grid', gap: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Room Name</label>
              <input
                type="text"
                value={roomName}
                onChange={e => setRoomName(e.target.value)}
                placeholder="e.g. Room 101"
                style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Room Type</label>
                {customRoomTypes.length > 0 ? (
                  <select
                    value={roomType}
                    onChange={e => setRoomType(e.target.value)}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                  >
                    <option value="">Select Room Type</option>
                    {customRoomTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                ) : (
                  <div style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, display: 'flex', alignItems: 'center', color: '#999', fontSize: 14 }}>
                    Add types to select
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Room Type (Add New)</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    type="text"
                    value={roomTypeInput}
                    onChange={e => setRoomTypeInput(e.target.value)}
                    placeholder="Enter room type"
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={handleAddRoomType}
                    style={{
                      padding: '10px 16px',
                      background: '#e67e22',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 14,
                      transition: 'background 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => (e.target.style.background = '#d17018')}
                    onMouseLeave={(e) => (e.target.style.background = '#e67e22')}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Operating Time</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
                    <input
                      type="checkbox"
                      checked={setDefaultOperatingTime}
                      onChange={e => setSetDefaultOperatingTime(e.target.checked)}
                    />
                    Set default
                  </label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input
                    type="time"
                    value={roomStartTime}
                    onChange={e => setRoomStartTime(e.target.value)}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                  />
                  <input
                    type="time"
                    value={roomEndTime}
                    onChange={e => setRoomEndTime(e.target.value)}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Operating Days</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
                    <input
                      type="checkbox"
                      checked={setDefaultOperatingDays}
                      onChange={e => setSetDefaultOperatingDays(e.target.checked)}
                    />
                    Set default
                  </label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <label key={day} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#22303c' }}>
                      <input
                        type="checkbox"
                        checked={operatingDays.includes(day)}
                        onChange={() => toggleOperatingDay(day)}
                      />
                      {day}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 12, padding: 16, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Days Specific Time (Optional)</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
                  <input
                    type="checkbox"
                    checked={setDefaultSpecificDay}
                    onChange={e => setSetDefaultSpecificDay(e.target.checked)}
                  />
                  Set default
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <select
                  value={specificDayDate}
                  onChange={e => setSpecificDayDate(e.target.value)}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
                >
                  <option value="">Select day</option>
                  <option value="Sun">Sun</option>
                  <option value="Mon">Mon</option>
                  <option value="Tue">Tue</option>
                  <option value="Wed">Wed</option>
                  <option value="Thu">Thu</option>
                  <option value="Fri">Fri</option>
                  <option value="Sat">Sat</option>
                </select>
                <input
                  type="time"
                  value={specificDayStartTime}
                  onChange={e => setSpecificDayStartTime(e.target.value)}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                />
                <input
                  type="time"
                  value={specificDayEndTime}
                  onChange={e => setSpecificDayEndTime(e.target.value)}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <button
                type="button"
                onClick={handleAddSpecificDayEntry}
                style={{ width: '180px', padding: 12, background: '#155c2b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              >
                Add specific day time
              </button>
              {specificDayEntries.length > 0 && (
                <div style={{ display: 'grid', gap: 8 }}>
                  {specificDayEntries.map(entry => (
                    <div key={entry.date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      <div style={{ color: '#22303c', fontSize: 13 }}>
                        {entry.date}: {entry.startTime} - {entry.endTime}
                      </div>
                      <button type="button" onClick={() => handleRemoveSpecificDayEntry(entry.date)} style={{ background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', gap: 36, minWidth: 0, height: '100%' }}>
            <div style={{ width: '100%', background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', boxSizing: 'border-box' }}>
              <label style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Departments Assigned (Optional - Multiple)</label>
              {selectedDepartments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, marginBottom: 8 }}>
                  {selectedDepartments.map(dept => (
                    <div
                      key={dept.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        background: '#e8f5e9',
                        border: '1px solid #81c784',
                        borderRadius: 16,
                        fontSize: 13,
                        color: '#2e7d32'
                      }}
                    >
                      {dept.name}
                      <button
                        type="button"
                        onClick={() => setSelectedDepartmentIds(prev => prev.filter(id => id !== dept.id))}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#2e7d32',
                          fontSize: 16,
                          padding: 0,
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={roomDepartmentInput}
                  onChange={e => { setRoomDepartmentInput(e.target.value); setShowDepartmentSuggestions(true); }}
                  onFocus={() => setShowDepartmentSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowDepartmentSuggestions(false), 150)}
                  placeholder="Type to search and add departments"
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                />
                {showDepartmentSuggestions && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: selectedDepartments.length > 0 ? 120 : 56, zIndex: 20 }}>
                    <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 6, maxHeight: 200, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                      {departmentSuggestions.length === 0 ? (
                        <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 13 }}>
                          No departments match
                        </div>
                      ) : (
                        departmentSuggestions.map(dept => (
                          <div
                            key={dept.id}
                            onMouseDown={() => handleToggleDepartment(dept.id)}
                            style={{
                              padding: 10,
                              cursor: 'pointer',
                              borderBottom: '1px solid #f6f6f6',
                              background: selectedDepartmentIds.includes(dept.id) ? '#f0f7ff' : '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDepartmentIds.includes(dept.id)}
                              onChange={() => {}}
                              style={{ cursor: 'pointer', margin: 0 }}
                            />
                            {dept.name}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <label style={{ fontWeight: 700, color: '#22303c', fontSize: 14 }}>Subjects (Optional - Multiple)</label>
              {selectedSubjects.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, marginBottom: 8 }}>
                  {selectedSubjects.map(s => {
                    const display = `${s.courseId || ''}${s.courseId ? ' - ' : ''}${s.courseName || ''}`;
                    return (
                      <div
                        key={s.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          background: '#e8f5e9',
                          border: '1px solid #81c784',
                          borderRadius: 16,
                          fontSize: 13,
                          color: '#2e7d32'
                        }}
                      >
                        {display}
                        <button
                          type="button"
                          onClick={() => handleRemoveSubject(s.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#2e7d32',
                            fontSize: 16,
                            padding: 0,
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={roomSubjectInput}
                  onChange={e => { setRoomSubjectInput(e.target.value); setShowSubjectSuggestions(true); }}
                  onFocus={() => setShowSubjectSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSubjectSuggestions(false), 150)}
                  placeholder="Type to search and add subjects"
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', height: 42, fontSize: 14, boxSizing: 'border-box' }}
                />
                {showSubjectSuggestions && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: selectedSubjects.length > 0 ? 120 : 56, zIndex: 20 }}>
                    <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 6, maxHeight: 200, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                      {subjectSuggestions.length === 0 ? (
                        <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 13 }}>
                          No subjects match
                        </div>
                      ) : (
                        subjectSuggestions.map(s => {
                          const display = `${s.courseId || ''}${s.courseId ? ' - ' : ''}${s.courseName || ''}`;
                          const isSelected = selectedSubjectIds.includes(s.id);
                          return (
                            <div
                              key={s.id}
                              onMouseDown={() => handleToggleSubject(s.id)}
                              style={{
                                padding: 10,
                                cursor: 'pointer',
                                borderBottom: '1px solid #f6f6f6',
                                background: isSelected ? '#f0f7ff' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                style={{ cursor: 'pointer', margin: 0 }}
                              />
                              {display}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, alignItems: 'center' }}>
              {isEditingRoom && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  style={{
                    width: '100px',
                    padding: 12,
                    background: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 14,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => (e.target.style.background = '#efefef')}
                  onMouseLeave={(e) => (e.target.style.background = '#f5f5f5')}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  width: isEditingRoom ? '100px' : '140px',
                  padding: 12,
                  background: '#155c2b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => !isSaving && (e.target.style.background = '#0d3d1f')}
                onMouseLeave={(e) => !isSaving && (e.target.style.background = '#155c2b')}
              >
                {isSaving ? 'Saving...' : (isEditingRoom ? 'Update Room' : 'Save Room')}
              </button>
            </div>
          </div>
        </form>

        {roomMsg && (
          <div style={{ marginTop: 18, padding: 12, borderRadius: 6, background: roomMsg.includes('successfully') ? '#d4edda' : '#f8d7da', color: roomMsg.includes('successfully') ? '#155724' : '#721c24', fontSize: 14 }}>
            {roomMsg}
          </div>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 600, color: '#155c2b', marginBottom: 24 }}>Existing Rooms</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 24 }}>
          <input
            type="text"
            placeholder="Filter by Room Name"
            aria-label="Filter by room name"
            value={roomFilterName}
            onChange={e => setRoomFilterName(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box' }}
          />
          <select
            aria-label="Filter by room type"
            value={roomFilterType}
            onChange={e => setRoomFilterType(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box' }}
          >
            <option value="">All Room Types</option>
            {[...new Set(rooms.map(room => room.roomType).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map(type => <option key={type} value={type}>{type}</option>)}
          </select>
          <select
            aria-label="Filter by assigned department"
            value={roomFilterDepartment}
            onChange={e => setRoomFilterDepartment(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box' }}
          >
            <option value="">All Departments Assigned</option>
            {[...new Set(rooms.flatMap(room => Array.isArray(room.departmentAssignedNames) ? room.departmentAssignedNames : [] ).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map(department => <option key={department} value={department}>{department}</option>)}
          </select>
          <select
            aria-label="Filter by assigned subject"
            value={roomFilterSubject}
            onChange={e => setRoomFilterSubject(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box' }}
          >
            <option value="">All Subjects Assigned</option>
            {[...new Set(rooms.flatMap(room => Array.isArray(room.roomSubjectNames) ? room.roomSubjectNames : room.roomSubjectName ? [room.roomSubjectName] : []).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map(subject => <option key={subject} value={subject}>{subject}</option>)}
          </select>
          <button
            type="button"
            onClick={() => {
              setRoomFilterName('');
              setRoomFilterType('');
              setRoomFilterSubject('');
              setRoomFilterDepartment('');
            }}
            disabled={!roomFilterName && !roomFilterType && !roomFilterSubject && !roomFilterDepartment}
            style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', background: roomFilterName || roomFilterType || roomFilterSubject || roomFilterDepartment ? '#fff' : '#f3f4f6', cursor: roomFilterName || roomFilterType || roomFilterSubject || roomFilterDepartment ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600 }}
          >
            Clear Filters
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', background: '#fff', fontSize: 14 }}>
            <thead style={{ background: '#f5f7fa' }}>
              <tr>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Room Name</th>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Type</th>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Departments Assigned</th>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Subjects Assigned</th>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Operating Time</th>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Operating Days</th>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Days Specific Time</th>
                <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: '#2d3748', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRooms.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 18, textAlign: 'center', color: '#667f92' }}>
                    No rooms match the filter.
                  </td>
                </tr>
              ) : filteredRooms.map((room, idx) => (
                <tr key={room.id} style={{ background: idx % 2 === 0 ? '#fafbfc' : '#fff' }}>
                  <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{room.roomName}</td>
                  <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{room.roomType}</td>
                  <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>
                    {room.departmentAssignedNames && room.departmentAssignedNames.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {room.departmentAssignedNames.map((name, i) => (
                          <div key={i} style={{ fontSize: 13, color: '#2e7d32', background: '#f0f9f0', padding: '2px 6px', borderRadius: 3 }}>
                            {name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>
                    {room.roomSubjectNames && room.roomSubjectNames.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {room.roomSubjectNames.map((name, i) => (
                          <div key={i} style={{ fontSize: 13, color: '#2e7d32', background: '#f0f9f0', padding: '2px 6px', borderRadius: 3 }}>
                            {name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>
                    {(room.roomStartTime || '') && (room.roomEndTime || '') ? `${formatTime12Hour(room.roomStartTime)} - ${formatTime12Hour(room.roomEndTime)}` : '—'}
                  </td>
                  <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>
                    {room.operatingDays && room.operatingDays.length > 0 ? room.operatingDays.join(', ') : '—'}
                  </td>
                  <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>
                    {room.specificDayEntries && room.specificDayEntries.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {room.specificDayEntries.map((entry, i) => (
                          <div key={i} style={{ fontSize: 13, color: '#2e7d32', background: '#f0f9f0', padding: '2px 6px', borderRadius: 3 }}>
                            {entry.date}: {formatTime12Hour(entry.startTime)} - {formatTime12Hour(entry.endTime)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ padding: 10, border: '1px solid #e5e7eb', position: 'relative', minWidth: 64, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => toggleActionMenu(room.id)}
                      style={{
                        width: 30,
                        height: 30,
                        padding: 0,
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        color: '#374151',
                        fontSize: 18,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => (e.target.style.background = '#e5e7eb')}
                      onMouseLeave={(e) => (e.target.style.background = '#f3f4f6')}
                    >
                      ⋮
                    </button>
                    {activeActionMenu === room.id && (
                      <div style={{
                        position: 'absolute',
                        top: 38,
                        right: '50%',
                        transform: 'translateX(50%)',
                        width: 130,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 10,
                        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)',
                        zIndex: 20,
                        overflow: 'hidden'
                      }}>
                        <button
                          type="button"
                          onClick={() => { handleEditRoom(room); setActiveActionMenu(null); }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'transparent',
                            border: 'none',
                            textAlign: 'left',
                            color: '#1f2937',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 13,
                            borderBottom: '1px solid #e5e7eb'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => { handleDeleteRoom(room); setActiveActionMenu(null); }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'transparent',
                            border: 'none',
                            textAlign: 'left',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 13
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DeleteConfirmationModal
        open={Boolean(roomPendingDelete)}
        title="Delete Room?"
        subtitle="Confirm room deletion"
        name={roomPendingDelete?.roomName || "this room"}
        message={`Are you sure you want to delete ${roomPendingDelete?.roomName || "this room"}? This action cannot be undone.`}
        detail="Please verify the room details before proceeding."
        error={roomDeleteError}
        deleting={isDeletingRoom}
        confirmLabel="Delete Room"
        onCancel={() => { setRoomPendingDelete(null); setRoomDeleteError(''); }}
        onConfirm={confirmDeleteRoom}
      />
    </div>
  );
}
